import React, { useRef, useState, useEffect, Suspense } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import robotoFont from '../../../fonts/Sealight Whisper_Regular.json';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { OrbitControls } from '@react-three/drei';
import {  mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CSG } from 'three-csg-ts';
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";


const font = new FontLoader().parse(robotoFont);

const defaultDIM = {
  depth: 4.5,
  textSize: 16,
  textHeight: 4.5,
  yOffset: 0,
  padding: 4,
  contourScale: 0.45,
};


// Crear mesh de texto extruido
function crearTextoMesh(texto, size, height, yOffset, zOffset = 0.1, baseDepth = 4.5) {
  const geo = new TextGeometry(texto, { font, size, height, curveSegments: 2 });
  geo.computeBoundingBox();
  geo.center();
  const mat = new THREE.MeshStandardMaterial({ color: '#000000' });
  const mesh = new THREE.Mesh(geo, mat);

  mesh.position.y = yOffset;
  mesh.position.z = baseDepth-1; // Lo ponemos justo encima de la base

  return mesh;
}
// Crear base personalizada según bounding box del texto
function crearBasePersonalizadaMesh(texto, dim, color) {
  const textGeo = new TextGeometry(texto, { font, size: dim.textSize, height: dim.textHeight, curveSegments: 2 });
  textGeo.computeBoundingBox();
  const bbox = textGeo.boundingBox;
  const w = bbox.max.x - bbox.min.x + dim.padding * 2;
  const h = bbox.max.y - bbox.min.y + dim.padding * 2;
  const shape = new THREE.Shape();
  const r = dim.padding; // radio para esquinas redondeadas
  shape.moveTo(-w / 2 + r, -h / 2);
  shape.lineTo(w / 2 - r, -h / 2);
  shape.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
  shape.lineTo(w / 2, h / 2 - r);
  shape.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2);
  shape.lineTo(-w / 2 + r, h / 2);
  shape.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r);
  shape.lineTo(-w / 2, -h / 2 + r);
  shape.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + r, -h / 2);
  const geo = new THREE.ExtrudeGeometry(shape, { depth: dim.depth, bevelEnabled: false });
  geo.center();
  const mat = new THREE.MeshStandardMaterial({ color });
  return new THREE.Mesh(geo, mat);
}
function crearBaseContornoMesh(nombre, dim, color) {
    const shapes = font.generateShapes(nombre, dim.textSize);
  
    const textGeo = new TextGeometry(nombre, {
      font,
      size: dim.textSize,
      height: dim.textHeight,
      curveSegments: 12,
    });
  
    textGeo.computeBoundingBox();
    const bbox = textGeo.boundingBox;
    const centerX = (bbox.max.x + bbox.min.x) / 2;
    const centerY = (bbox.max.y + bbox.min.y) / 2;
  
    // Obtener puntos y desplazarlos radialmente (offset tipo contorno)
    const expandedShapes = shapes.map((shape) => {
      const points = shape.getPoints(5); // más puntos = más suavidad
      const offsetPoints = [];
  
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const next = points[(i + 1) % points.length];
        const prev = points[(i - 1 + points.length) % points.length];
  
        // calcular dirección normal a este punto
        const dx = next.y - prev.y;
        const dy = -(next.x - prev.x);
        const length = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = (dx / length) * dim.padding * (dim.contourScale - 1);
        const ny = (dy / length) * dim.padding * (dim.contourScale - 1);
  
        offsetPoints.push(new THREE.Vector2(p.x + nx, p.y + ny));
      }
  
      const newShape = new THREE.Shape(offsetPoints);
      return newShape;
    });
  
    const geometry = new THREE.ExtrudeGeometry(expandedShapes, {
      depth: dim.depth,
      bevelEnabled: 1,
      bevelSize: 1,
      bevelSegments: 4,
      bevelThickness: 0.5,
    });
  
    geometry.center();
    const material = new THREE.MeshStandardMaterial({ color });
    return new THREE.Mesh(geometry, material);
  }
function crearBaseSplineMesh(texto, dim, color) {
    const textGeo = new TextGeometry(texto, { font, size: dim.textSize, height: dim.textHeight, curveSegments: 2 });
    textGeo.computeBoundingBox();
    const verts = textGeo.attributes.position.array;
    // extraemos un perfil aproximado: usamos bounds + padding y suavizamos con spline
    const minX = textGeo.boundingBox.min.x, maxX = textGeo.boundingBox.max.x;
    const minY = textGeo.boundingBox.min.y, maxY = textGeo.boundingBox.max.y;
    const pad = dim.padding;
    const pts2d = [
      new THREE.Vector2(minX - pad, minY - pad),
      new THREE.Vector2(minX - pad, maxY + pad),
      new THREE.Vector2(maxX + pad, maxY + pad),
      new THREE.Vector2(maxX + pad, minY - pad),
    ];
    const curve = new THREE.CatmullRomCurve3(pts2d.map(p => new THREE.Vector3(p.x, p.y, 0)), true, 'catmullrom', 0.1);
    const splinePts = curve.getPoints(5).map(v => new THREE.Vector2(v.x, v.y));
    const shape = new THREE.Shape(splinePts);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: dim.depth, bevelEnabled: false });
    geo.center();
    const mat = new THREE.MeshStandardMaterial({ color });
    return new THREE.Mesh(geo, mat);
  }
  function crearBaseRectMesh(texto, dim, color) {
    const tgeo = new TextGeometry(texto, { font, size: dim.textSize, height: dim.textHeight, curveSegments: 2 });
    tgeo.computeBoundingBox();
    const bb = tgeo.boundingBox;
    const w = bb.max.x - bb.min.x + dim.padding * 2;
    const h = bb.max.y - bb.min.y + dim.padding * 2;
    const r = dim.padding;
    const shape = new THREE.Shape();
    shape.moveTo(-w/2+r, -h/2);
    shape.lineTo(w/2-r, -h/2);
    shape.quadraticCurveTo(w/2, -h/2, w/2, -h/2 + r);
    shape.lineTo(w/2, h/2 - r);
    shape.quadraticCurveTo(w/2, h/2, w/2 - r, h/2);
    shape.lineTo(-w/2 + r, h/2);
    shape.quadraticCurveTo(-w/2, h/2, -w/2, h/2 - r);
    shape.lineTo(-w/2, -h/2 + r);
    shape.quadraticCurveTo(-w/2, -h/2, -w/2 + r, -h/2);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: dim.depth, bevelEnabled: false });
    geo.center();
    const mat = new THREE.MeshStandardMaterial({ color });
    return new THREE.Mesh(geo, mat);
  }
// --- crearBaseCorazonMesh (arreglada: sin escala negativa, normals, color parametrizable, intento de CSG) ---
function crearBaseCorazonMesh(texto, dim, color) { 
  const heartShape = new THREE.Shape(); 
  heartShape.moveTo(0, 5); 
  heartShape.bezierCurveTo(0, 5, -4, 0, -8, 0); 
  heartShape.bezierCurveTo(-14, 0, -14, 8, -14, 8); 
  heartShape.bezierCurveTo(-14, 13, -8, 20, 0, 25); 
  heartShape.bezierCurveTo(8, 20, 14, 13, 14, 8); 
  heartShape.bezierCurveTo(14, 8, 14, 0, 8, 0); 
  heartShape.bezierCurveTo(4, 0, 0, 5, 0, 5); 
  const heartGeo = new THREE.ExtrudeGeometry(heartShape, { depth: dim.depth, bevelEnabled: false, }); 
  heartGeo.center(); 
  const scale = 2.6; // puedes ajustar entre 1.0 y 2.0 si lo quieres más o menos grande 
  heartGeo.scale(scale, scale, 1); 
  const heartMat = new THREE.MeshStandardMaterial({ color }); 
  const heartMesh = new THREE.Mesh(heartGeo, heartMat); 
  const contornoMesh = crearBaseContornoMesh(texto, dim, '#ff0000'); 
  const group = new THREE.Group(); group.add(heartMesh); 
  group.add(contornoMesh); 


  contornoMesh.position.z = -dim.depth / 2 + 2.8;
contornoMesh.updateMatrix();                      // 🔹 Actualiza su matriz de transformación
contornoMesh.geometry.applyMatrix4(contornoMesh.matrix); // 🔹 Aplica la posición a la geometría real
contornoMesh.position.set(0, 0, 0);               // 🔹 Resetea posición para evitar duplicar en vista



  heartMesh.rotation.z = Math.PI; // Gira 180 grados para que quede al derecho
  heartMesh.updateMatrix(); // 🔹 Aplica la rotación a su matriz
  heartGeo.applyMatrix4(heartMesh.matrix); // 🔹 Hornea la rotación en la geometría

  heartMesh.rotation.set(0, 0, 0); // 🔹 Resetea la rotación para evitar duplicados en vista
  heartMesh.position.z = 0;

  return group;

  


 }
// Crea una estrella de 5 puntas con un contorno y texto extruido encima
function crearBaseEstrellaMesh(texto, dim, color = "#FFD700") {
  // Crear forma de estrella
  const starShape = new THREE.Shape();
  const outerRadius = 52;
  const innerRadius = 30;
  const spikes = 5;
  const step = Math.PI / spikes;
  let rot = Math.PI / 2 * 3;
  let x = 0;
  let y = 0;

  starShape.moveTo(0, -outerRadius);
  for (let i = 0; i < spikes; i++) {
    x = Math.cos(rot) * outerRadius;
    y = Math.sin(rot) * outerRadius;
    starShape.lineTo(x, y);
    rot += step;

    x = Math.cos(rot) * innerRadius;
    y = Math.sin(rot) * innerRadius;
    starShape.lineTo(x, y);
    rot += step;
  }
  starShape.lineTo(0, -outerRadius);
  starShape.closePath();

  // Extruir la forma
  const starGeo = new THREE.ExtrudeGeometry(starShape, {
    depth: dim.depth,
    bevelEnabled: false,
  });
  starGeo.center();

  const starMat = new THREE.MeshStandardMaterial({ color });
  const starMesh = new THREE.Mesh(starGeo, starMat);

  // 🔁 Girar solo la estrella
  starMesh.rotation.z = Math.PI;

  // Crear contorno del texto
  const contornoMesh = crearBaseContornoMesh(texto, dim, "#FFD700");
  contornoMesh.position.z = -dim.depth / 2 + 2.8;
  contornoMesh.position.set(0, 0, 0.5);

  // Texto sobre la estrella
  const textoMesh = crearTextoMesh(texto, dim.textSize, dim.textHeight, dim.yOffset, 0.1, dim.depth);
  textoMesh.position.z = dim.depth / 2;

  // Agrupar todo
  const group = new THREE.Group();
  group.add(starMesh);
  group.add(contornoMesh);
  group.add(textoMesh);

  return group;
}
// --- crearBaseCopoMesh MEJORADO: con detección, centrado, box helpers y escalado automático ---
export async function crearBaseCopoMesh(texto, dim, color = "#87CEFA") {
  const loader = new STLLoader();
  let geometry;

  try {
    // 🔹 Cargar STL del copo desde /public/models
    geometry = await new Promise((resolve, reject) => {
      loader.load(`${process.env.PUBLIC_URL}/models/copo_nieve.STL`, // ✅ opción 2
        (geo) => resolve(geo),
        undefined,
        (err) => reject(err)
      );
    });
    console.log("✅ STL cargado correctamente");
  } catch (err) {
    console.error("❌ Error al cargar copo_nieve.stl:", err);
    // fallback: cubo placeholder
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(10, 10, dim.depth),
      new THREE.MeshStandardMaterial({ color: "red", opacity: 0.4, transparent: true })
    );
    const txt = crearTextoMesh(texto, dim.textSize, dim.textHeight, dim.yOffset, "#ffffff", dim.depth);
    const g = new THREE.Group();
    g.add(cube);
    g.add(txt);
    return g;
  }

  // 🔹 Crear material y mesh base del copo
  const mat = new THREE.MeshStandardMaterial({
    color,
    metalness: 0.3,
    roughness: 0.6,
  });

  const copoMesh = new THREE.Mesh(geometry, mat);
  geometry.computeBoundingBox();

  // 🔹 Centrar y escalar el copo a un tamaño estándar (~50 unidades)
  const bbox = geometry.boundingBox;
  const sizeX = bbox.max.x - bbox.min.x;
  const sizeY = bbox.max.y - bbox.min.y;
  const targetSize = 50;
  const scaleFactor = targetSize / Math.max(sizeX, sizeY);
  copoMesh.scale.set(scaleFactor * 2, scaleFactor * 2, scaleFactor * 2);
  copoMesh.geometry.center();

  console.log(`🧮 Escalado aplicado: ×${scaleFactor.toFixed(3)}`);

  copoMesh.position.set(0, 0, -dim.depth / 2);
  copoMesh.rotation.x = Math.PI; // invertido para quedar derecho


  // --- parámetros específicos para copo: contorno más plano y texto menos extruido
  const contourDim = { ...dim, depth: Math.max(0.8, (dim.textHeight || 1) * 1.2) }; 
  // (usa 0.6x la altura del texto para el contorno, mínimo 0.8 mm para que no sea 0)

  const contornoMesh = crearBaseContornoMesh(texto, contourDim, color);
  // situar ligeramente por encima/fuera del copo para que no interseccione tanto
  contornoMesh.position.z = -dim.depth / 2 + 1.3;

  // reducir la extrusión del texto sólo aquí
  const textoHeightForCopo = Math.max(0.6, (dim.textHeight || 1) * 0.7); // ajusta factor 0.5 a gusto
  const textoMesh = crearTextoMesh(
    texto,
    dim.textSize,
    textoHeightForCopo,
    dim.yOffset,
    "#ffffff",
    dim.depth
  );
  // colocar justo sobre el contorno
  textoMesh.position.z = contornoMesh.position.z + 3;


  // 🔹 Agrupar todo
  const group = new THREE.Group();

  // Añadir solo objetos válidos (no añadir duplicados)
  const safeAdd = (obj) => {
    if (obj && obj.isObject3D) group.add(obj);
  };

  safeAdd(copoMesh);
  safeAdd(contornoMesh);
  safeAdd(textoMesh);

  // Crear helpers/luces correctamente (no encadenando .position.set)
  const helper = new THREE.BoxHelper(copoMesh, 0x00aaff);
  safeAdd(helper);

  const light = new THREE.DirectionalLight(0xffffff, 1.2);
  light.position.set(50, 80, 100);
  safeAdd(light);

  const ambient = new THREE.AmbientLight(0xffffff, 0.6);
  safeAdd(ambient);

  const point = new THREE.PointLight(0xffffff, 2, 300);
  point.position.set(40, 40, 40);
  safeAdd(point);

  const axes = new THREE.AxesHelper(100);
  safeAdd(axes);

  group.tick = (delta) => {
    group.rotation.y += delta * 0.5;
  };

  return group;
}
// --- MODIFICACIONES EN crearAroLlaveroMesh (solo ajuste menor en Z para centrar con la base) ---
function crearAroLlaveroMesh(diametroInterior, grosor, posicionX = 0, posicionY = 0, posicionZ = null, profundidad = 4.5) {
  const radioInterior = diametroInterior / 2;
  const radioExterior = radioInterior + grosor;
  const shape = new THREE.Shape();
  shape.absarc(0, 0, radioExterior, 0, Math.PI * 2, false);
  const hole = new THREE.Path();
  hole.absarc(0, 0, radioInterior, 0, Math.PI * 2, true);
  shape.holes.push(hole);

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: profundidad,
    bevelEnabled: false,
  });

  const material = new THREE.MeshStandardMaterial({ color: '#000000' });
  const mesh = new THREE.Mesh(geometry, material);

  // Si no se pasa posicionZ usamos la lógica antigua, si se pasa usamos ese z exacto
  const defaultZ = -profundidad / 2 + 0.01;
  mesh.position.set(posicionX, posicionY, (typeof posicionZ === 'number') ? posicionZ : defaultZ);

  return mesh;
}
 
function BaseYTexto({ nombre, color, dim, modo, agregarLlavero, diametroAgujero, grosorAro, posicionAroX ,posicionAroY, posicionAroZ }) {
    const ref = useRef();
  
    useEffect(() => {
      const group = new THREE.Group();
    
      const crear = async () => {
        let baseMesh;
        if (modo === "contorno") baseMesh = crearBaseContornoMesh(nombre, dim, color);
        else if (modo === "corazon") baseMesh = crearBaseCorazonMesh(nombre, dim, color);
        else if (modo === "estrella") baseMesh = crearBaseEstrellaMesh(nombre, dim, color);
        else if (modo === "copo") baseMesh = await crearBaseCopoMesh(nombre, dim, color); 
        else baseMesh = crearBaseRectMesh(nombre, dim, color);
    
        group.add(baseMesh);
        group.add(crearTextoMesh(nombre, dim.textSize, dim.textHeight, dim.yOffset, 0.1, dim.depth));
    
        if (agregarLlavero) {
          // 🔹 Si el modo es estrella, usar grosor fijo de 8 mm
          const grosorFinal = (modo === "estrella") ? 8 : grosorAro;
          const aroMesh = crearAroLlaveroMesh(
            diametroAgujero,
            grosorFinal,
            posicionAroX,
            posicionAroY,
            posicionAroZ,
            dim.depth
          );
          group.add(aroMesh);
        }
        
    
        if (ref.current) {
          ref.current.children.forEach(c => {
            c.traverse(obj => {
              if (obj.isMesh) {
                obj.geometry?.dispose?.();
                obj.material?.dispose?.();
              }
            });
            ref.current.remove(c);
          });
        
          ref.current.add(...group.children);
          ref.current.updateMatrixWorld(true);
        }

        
      };
    
      crear(); // 👈 ejecutamos la función asíncrona
    }, [nombre, color, dim, modo, agregarLlavero, diametroAgujero, grosorAro, posicionAroX, posicionAroY]);
    
    return <group ref={ref} />;
}
  
// arriba ya importaste: mergeGeometries (BufferGeometryUtils), STLExporter, THREE, etc.
// Para la opción CSG (unir sólidos) instala: npm i three-csg-ts
// y luego importa si la vas a usar:
// import { CSG } from 'three-csg-ts';



// --- ExportarSTL actualizado: maneja Groups y usa mergeVertices para reducir tamaño ---
// --- ExportarSTL (fragmento) ---

// --- util: recolecta geometrías de un Object3D o array de Object3D ---
// helpers: collectGeometriesFromObject
function collectGeometriesFromObject(root) {
  const geos = [];
  if (!root) return geos;

  root.updateMatrixWorld(true);

  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;

    if (node.isMesh && node.geometry) {
      let g = node.geometry.clone();
      g.applyMatrix4(node.matrixWorld);

      // 🔹 Asegurar atributos mínimos requeridos
      if (!g.getAttribute('position')) continue;
      if (!g.getAttribute('normal')) {
        g.computeVertexNormals();
      }

      // 🔹 Eliminar atributos innecesarios (uv, color, etc.) para compatibilidad
      const attrs = g.attributes;
      for (const key in attrs) {
        if (key !== 'position' && key !== 'normal') {
          g.deleteAttribute(key);
        }
      }

      geos.push(g);
    }

    if (Array.isArray(node.children)) {
      for (let i = 0; i < node.children.length; i++) {
        const c = node.children[i];
        if (c) stack.push(c);
      }
    }
  }

  return geos;
}

// ExportarSTL: manejar async y await
function ExportarSTL(props) {
  const [exportando, setExportando] = useState(false);
  const handle = async (e) => {
    e?.preventDefault?.(); // por si está dentro de un form
    if (exportando) return;
    setExportando(true);
  
    try {
      console.log("🔍 Inicio exportación...");
  
      // 1) crear base (podría ser Promise)
      let baseMesh;
      if (props.modo === "contorno") baseMesh = crearBaseContornoMesh(props.nombre, props.dim, props.color);
      else if (props.modo === "corazon") baseMesh = crearBaseCorazonMesh(props.nombre, props.dim, props.color);
      else if (props.modo === "estrella") baseMesh = crearBaseEstrellaMesh(props.nombre, props.dim, props.color);
      else if (props.modo === "copo") baseMesh = crearBaseCopoMesh(props.nombre, props.dim, props.color);
      else baseMesh = crearBaseRectMesh(props.nombre, props.dim, props.color);
  
      if (baseMesh && typeof baseMesh.then === "function") {
        console.log("⏳ Esperando baseMesh (promise)...");
        baseMesh = await baseMesh;
      }
      if (!baseMesh) throw new Error("Base vacía tras resolución");
  
      // remover luces/helpers para exportar (reunir nodos y remover fuera de traverse)
      const nodesToRemove = [];
      baseMesh.traverse((c) => {
        if (!c) return;
        if (c.isLight || (c.type && /Helper$/.test(c.type))) nodesToRemove.push(c);
      });
      nodesToRemove.forEach((c) => { if (c.parent) c.parent.remove(c); });
  
      baseMesh.updateMatrixWorld(true);
  
      console.log("Children de baseMesh:", baseMesh.children);
  
      // recolectar geometrías
      const allGeos = collectGeometriesFromObject(baseMesh);
  
      // texto
      const textoMesh = crearTextoMesh(props.nombre, props.dim.textSize, props.dim.textHeight, props.dim.yOffset, 0.1, props.dim.depth);
      if (textoMesh && typeof textoMesh.then === "function") {
        const t = await textoMesh;
        t.updateMatrixWorld(true);
        allGeos.push(...collectGeometriesFromObject(t));
      } else {
        textoMesh.updateMatrixWorld(true);
        allGeos.push(...collectGeometriesFromObject(textoMesh));
      }
  
      //
      if (props.agregarLlavero) {
        // 🔹 Si el modo es estrella, el aro debe ser de 8 mm
        const grosorFinal = (props.modo === "estrella") ? 8 : props.grosorAro;
        const aro = crearAroLlaveroMesh(
          props.diametroAgujero,
          grosorFinal,
          props.posicionAroX,
          props.posicionAroY,
          props.posicionAroZ,
          props.dim.depth
        );
      

  // 🔹 aplicar matriz local ANTES de recolectar la geometría
  aro.updateMatrix(); // asegura que position/rotation/scale estén en la matriz local
  aro.geometry.applyMatrix4(aro.matrix); // hornea la transformación en la geometría

  // 🔹 resetear posición (opcional, para no duplicar transformaciones)
  aro.position.set(0, 0, 0);

  // 🔹 actualizar matriz global
  aro.updateMatrixWorld(true);

  allGeos.push(...collectGeometriesFromObject(aro));
}

  
      if (allGeos.length === 0) throw new Error("No geometrías para exportar");
  
      // Filtrar geometrías inválidas o vacías
      const validGeos = allGeos.filter(g => g && g.getAttribute && g.getAttribute('position')?.count > 0);
      if (validGeos.length === 0) throw new Error("No hay geometrías válidas para exportar");
      console.log(`🧩 Geometrías válidas para merge: ${validGeos.length}`);
  
      validGeos.forEach((g, i) => {
        console.log(`🔸 Geo ${i}:`, {
          position: g.getAttribute('position')?.count,
          normal: g.getAttribute('normal')?.count,
          attrs: Object.keys(g.attributes)
        });
      });
  
      let combined = mergeGeometries(validGeos, true);
      try { combined = mergeVertices(combined, 1e-4); } catch (err) { console.warn("mergeVertices falló:", err); }
      combined.computeVertexNormals();
      if (combined.toNonIndexed) combined = combined.toNonIndexed();
      try { combined = mergeVertices(combined, 1e-3); } catch (e) {}
  
      const meshFinal = new THREE.Mesh(combined, new THREE.MeshStandardMaterial());
      const exporter = new STLExporter();
  
      // ----- Aquí empieza la parte robusta para soportar cualquier tipo devuelto por exporter.parse -----
      const normalizeParseResult = async (res) => {
        // null/undefined
        if (res == null) return null;
  
        // string ASCII
        if (typeof res === "string") return res;
  
        // ArrayBuffer directamente
        if (res instanceof ArrayBuffer) return res;
  
        // Uint8Array -> ArrayBuffer
        if (res instanceof Uint8Array) return res.buffer;
  
        // Blob -> ArrayBuffer (await)
        if (typeof Blob !== "undefined" && res instanceof Blob) {
          return await res.arrayBuffer();
        }
  
        // objeto con .data (algunas versiones devuelven { data: ArrayBuffer } o { data: Uint8Array })
        if (typeof res === "object") {
          if (res.data) {
            if (res.data instanceof ArrayBuffer) return res.data;
            if (res.data instanceof Uint8Array) return res.data.buffer;
            if (typeof res.data === "string") return res.data;
          }
          // objeto tipo Response-like con arrayBuffer()
          if (typeof res.arrayBuffer === "function") {
            try {
              const ab = await res.arrayBuffer();
              if (ab instanceof ArrayBuffer) return ab;
            } catch (e) { /* ignore */ }
          }
          // Node Buffer (buffer property)
          if (res.buffer && res.buffer instanceof ArrayBuffer) return res.buffer;
        }
  
        // no reconocido
        return { unexpected: true, valueType: typeof res };
      };
  
      // Intenta parsear (primero binario, luego ASCII si falla)
      let parsedRaw;
      try {
        parsedRaw = exporter.parse(meshFinal, { binary: true });
      } catch (err) {
        console.warn("STLExporter.parse(binary) falló, intentando ASCII...", err);
        parsedRaw = exporter.parse(meshFinal, { binary: false });
      }
  
      const normalized = await normalizeParseResult(parsedRaw);
  
      // Si la normalización devolvió objeto inesperado, lanzar error con info para debug
      if (normalized && normalized.unexpected) {
        throw new Error("STLExporter devolvió un tipo inesperado: " + normalized.valueType);
      }
      if (normalized == null) throw new Error("STLExporter devolvió null/undefined");
  
      let blob;
      if (typeof normalized === "string") {
        console.log("📦 STL generado como texto ASCII (string)");
        blob = new Blob([normalized], { type: "text/plain" });
      } else if (normalized instanceof ArrayBuffer) {
        console.log("📦 STL generado como ArrayBuffer (binario)");
        blob = new Blob([normalized], { type: "application/octet-stream" });
      } else {
        throw new Error("Resultado del exporter no pudo convertirse en Blob (tipo: " + typeof normalized + ")");
      }
      // ----- fin normalización -----
  
      // crear enlace, añadir al DOM y forzar click
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `llavero_${props.nombre || "export"}_${Date.now()}.stl`;
      document.body.appendChild(a);
  
      console.log("🔽 Intentando iniciar descarga...");
      a.click();
  
      // cleanup
      setTimeout(() => {
        URL.revokeObjectURL(url);
        try { a.remove(); } catch (e) {}
      }, 1000);
  
      console.log("✅ Exportación terminada, si no viste descarga revisa consola y blockers.");
    } catch (err) {
      console.error("❌ Error exportando STL:", err);
      alert("Error al exportar. Mira la consola para más detalles.");
    } finally {
      setExportando(false);
    }
  };
  

  return (
    <button type="button" onClick={handle} disabled={exportando}>
      {exportando ? "Exportando..." : "📥 Descargar STL"}
    </button>
  );
}
  
function LuzControl({ luzRef, targetRef }) {
  useEffect(() => {
    if (luzRef.current && targetRef.current) luzRef.current.target = targetRef.current;
  }, [luzRef, targetRef]);
  return (
    <>
      <directionalLight ref={luzRef} position={[5, 5, 5]} intensity={1.2} castShadow />
      <object3D ref={targetRef} position={[0, 0, 0]} />
    </>
  );
}

function Vista3D({ nombre, color, dim, autoRotate, modo, agregarLlavero, diametroAgujero, grosorAro, posicionAroX, posicionAroY, posicionAroZ }) {
    const ref = useRef();
    const luzRef = useRef();
    const targetRef = useRef();
  
    useEffect(() => {
      const group = new THREE.Group();
    
      const crear = async () => {
        let baseMesh;
        if (modo === "contorno") baseMesh = crearBaseContornoMesh(nombre, dim, color);
        else if (modo === "corazon") baseMesh = crearBaseCorazonMesh(nombre, dim, color);
        else if (modo === "estrella") baseMesh = crearBaseEstrellaMesh(nombre, dim, color);
        else if (modo === "copo") baseMesh = await crearBaseCopoMesh(nombre, dim, color); 
        else baseMesh = crearBaseRectMesh(nombre, dim, color);
    
        group.add(baseMesh);
        group.add(crearTextoMesh(nombre, dim.textSize, dim.textHeight, dim.yOffset, 0.1, dim.depth));
    
        if (agregarLlavero) {
          const aroMesh = crearAroLlaveroMesh(diametroAgujero, grosorAro, posicionAroX, posicionAroY, posicionAroZ, dim.depth);
          group.add(aroMesh);
        }
    
        if (ref.current) {
          while (ref.current.children.length > 0) {
            const child = ref.current.children[0];
            ref.current.remove(child);
            child.geometry?.dispose?.();
            child.material?.dispose?.();
          }
          ref.current.add(...group.children);
        }
      };
    
      crear(); // 👈 ejecutamos la función asíncrona
    }, [nombre, color, dim, modo, agregarLlavero, diametroAgujero, grosorAro, posicionAroX, posicionAroY]);


    

    useFrame(() => {
      if (autoRotate && ref.current) ref.current.rotation.y += 0.01;
    });
  
    return (
      <group ref={ref}>
        <LuzControl luzRef={luzRef} targetRef={targetRef} />
      </group>
    );
}
  
  
  //-----------------------------------------------------------------------------------------
  //-----------------------------------------------------------------------------------------
  // --- HELPERS NUEVOS: calcular bounding boxes y posición automática del aro ---
function getTextBoundingBox(nombre, dim) {
  const tgeo = new TextGeometry(nombre, { font, size: dim.textSize, height: dim.textHeight, curveSegments: 2 });
  tgeo.computeBoundingBox();
  return tgeo.boundingBox.clone(); // { min, max }
}
function getBaseBoundingBoxApprox(nombre, dim, modo) {
  // Para rect: construimos el mismo rect que crearBaseRectMesh y devolvemos bbox en coordenadas centradas
  const textBbox = getTextBoundingBox(nombre, dim);
  if (modo === 'rect') {
    const w = (textBbox.max.x - textBbox.min.x) + dim.padding * 2;
    const h = (textBbox.max.y - textBbox.min.y) + dim.padding * 2;
    return {
      min: new THREE.Vector3(-w / 2, -h / 2, 0),
      max: new THREE.Vector3(w / 2, h / 2, 0),
    };
  } else {
    // modo 'contorno' (aprox): expandimos el bbox del texto por padding * contourScale
    const expand = dim.padding * Math.max(dim.contourScale, 1);
    return {
      min: new THREE.Vector3(textBbox.min.x - expand, textBbox.min.y - expand, 0),
      max: new THREE.Vector3(textBbox.max.x + expand, textBbox.max.y + expand, 0),
    };
  }
}
/**
 * calcularPosicionAroAuto:
 * intenta colocar el aro en esquina superior izquierda del "base bbox",
 * pero buscando que el agujero INTERNO no caiga sobre el texto. 
 * Devuelve { x, y } en coordenadas locales (mismo sistema que tus meshes centrados).
 */
function calcularPosicionAroAuto(nombre, dim, diametroInterior, grosorAro, modo) {
  const tgeo = new TextGeometry(nombre, { font, size: dim.textSize, height: dim.textHeight });
  tgeo.computeBoundingBox();
  const bbox = tgeo.boundingBox;

  const textWidth = bbox.max.x - bbox.min.x;
  const textHeight = bbox.max.y - bbox.min.y;

  const innerR = diametroInterior / 2;
  const outerR = innerR + grosorAro;

  let expandX = dim.padding * (modo === "contorno" ? dim.contourScale * 1.2 : 1);
  let expandY = dim.padding * (modo === "contorno" ? dim.contourScale * 1.2 : 1);

  const minX = -textWidth / 2 - expandX;
  const maxY = textHeight / 2 + expandY;

  let x, y, z;

  if (modo === "contorno") {
    x = minX - outerR * 0.1;
    y = maxY - outerR * 0.8;
    z = -dim.depth / 2 + 0.01; // por defecto sobre la base
  } else if (modo === "corazon") {
    const heartScale = 2.6;
    const heartHeight = 17 * heartScale;
    const heartTopY = heartHeight / 2;
    x = -25;
    y = heartTopY + outerR * 1.5;
    z = -dim.depth / 2 + 0.01;
    return { x: Math.round(x*100)/100, y: Math.round(y*100)/100, z: Math.round(z*100)/100 };
  }  else if (modo === "estrella") {
    const heartScale = 2.6;
    const heartHeight = 17 * heartScale;
    const heartTopY = heartHeight / 2;
    x = 0;
    y = heartTopY + outerR * 3.5;
    z = -dim.depth / 2 + 0.01;
    return { x: Math.round(x*100)/100, y: Math.round(y*100)/100, z: Math.round(z*100)/100 };
  } else if (modo === "copo") {
    // Aquí la magia: empujamos el aro ligeramente hacia "atrás" (más negativo)
    // Ajusta `penetration` en caso de que quieras más/menos.
    const penetration = 1.2; // valores típicos: 0.5 .. 2.5 — pruébalo con el slider si agregas UI
    // Centrar X para el copo (puedes ajustar si quieres otro offset)
    x = 0;
    // calcular Y relativo; usamos un valor por defecto basado en tamaño de copo/texto
    const baseTop = Math.max(textHeight / 2, 22); // fallback
    y = baseTop + outerR * 4;
    // z se coloca más atrás que la base
    z = -dim.depth / 1.5 - penetration;
    return { x: Math.round(x*100)/100, y: Math.round(y*100)/100, z: Math.round(z*100)/100 };
  } else {
    x = minX - outerR * 0.2;
    y = maxY - outerR * 0.2;
    z = -dim.depth / 2 + 0.01;
  }

  return {
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
    z: Math.round(z * 100) / 100,
  };
}
  //-----------------------------------------------------------------------------------------
  //-----------------------------------------------------------------------------------------

export default function GeneradorNombre() {
  const [nombre, setNombre] = useState('TuNombre');
  const [color, setColor] = useState('#ffcc00');
  const [dim, setDim] = useState(defaultDIM);
  const [autoRotate, setAutoRotate] = useState(true);
  const [formaBase, setFormaBase] = useState('rect');


  const [agregarLlavero, setAgregarLlavero] = useState(false);
  const [diametroAgujero, setDiametroAgujero] = useState(4.5); // mm
const [grosorAro, setGrosorAro] = useState(5.5); // mm
const [posicionAroX, setPosicionAroX] = useState(-10); // desplazamiento en X
const [posicionAroY, setPosicionAroY] = useState(25); // por defecto esquina superior izquierda
const [posicionAroZ, setPosicionAroZ] = useState(-dim.depth/2 + 0.01);




  // bandera para saber si queremos posicionamiento automático (si el usuario mueve sliders, se desactiva)
  const [autoPositionAro, setAutoPositionAro] = useState(true);

  // efecto: cuando se activa agregarLlavero (o cambia texto/dim/modo) y está en modo auto, calcular posición
  useEffect(() => {
    if (!agregarLlavero) return;
    if (!autoPositionAro) return;

    try {
      const { x, y, z } = calcularPosicionAroAuto(
        nombre,
        dim,
        diametroAgujero,
        grosorAro,
        formaBase
      );
      setPosicionAroX(x);
      setPosicionAroY(y);
      // si la función devolvió z, lo usamos; si no, mantenemos el valor actual
      if (typeof z === 'number') setPosicionAroZ(z);
    } catch (err) {
      console.warn('calcularPosicionAroAuto fallo:', err);
    }
    
  }, [agregarLlavero, nombre, dim, formaBase, diametroAgujero, grosorAro, autoPositionAro]);

  // cuando el usuario mueva manualmente sliders, desactivar auto-positioning
  const onManualMoveAro = (setter) => (val) => {
    setAutoPositionAro(false);
    setter(val);
  };

  return (
    <div style={{ padding: 20, background:"white" }}>
      <h2>🧩 Generador de Nombre STL</h2>
      <div style={{ margin: '10px 0' }}>
        <label>Nombre: <input value={nombre} onChange={e => setNombre(e.target.value)} /></label>
      </div>
      <div style={{ margin: '10px 0' }}>
        <label>Color base: <input type="color" value={color} onChange={e => setColor(e.target.value)} /></label>
      </div>
      <ExportarSTL
  nombre={nombre}
  color={color}
  dim={dim}
  modo={formaBase}
  agregarLlavero={agregarLlavero}
  diametroAgujero={diametroAgujero}
  grosorAro={grosorAro}
  posicionAroX={posicionAroX}
  posicionAroY={posicionAroY}
  posicionAroZ={posicionAroZ}
/>



      <div style={{ margin: '10px 0' }}>
  <label>Forma de base: </label>
  <select value={formaBase} onChange={e => {
  const val = e.target.value;
  setFormaBase(val);
  if (val === "corazon") {
    setColor("#ff6666");
  } else if (val === "copo") {
    setColor("#87CEFA"); // azul claro ❄️
  } else if (val === "contorno") {
    setColor("#ffcc00");
  } else if (val === "estrella") {
    setColor("#ffcc00");
  }  else {
    setColor("#ffcc00");
  }
}}>
  <option value="rect">Rectángulo</option>
  <option value="contorno">Contorno</option>
  <option value="corazon">Corazón</option>
  <option value="copo">Copo de Nieve</option> {/* ❄️ nueva opción */}
  <option value="estrella">Estrella</option>
</select>

</div>


<div style={{ margin: '10px 0' }}>
  <label>Escala del contorno: 
    <input 
      type="number" 
      value={dim.contourScale} 
      onChange={e => setDim(prev => ({ ...prev, contourScale: parseFloat(e.target.value) || 1 }))} 
      step="0.1"
    />
  </label>
</div>

<input
  type="checkbox"
  checked={agregarLlavero}
  onChange={e => {
    const checked = e.target.checked;
    setAgregarLlavero(checked);

    if (checked) {
      setAutoPositionAro(true);


      const modoAro = formaBase === 'contorno' ? 'contorno' : formaBase === 'corazon' ? 'corazon' : 'rect';
const { x, y } = calcularPosicionAroAuto(nombre, dim, diametroAgujero, grosorAro, modoAro);

      // 🔁 Posicionar inmediatamente el aro en la esquina superior izquierda
      setPosicionAroX(x);
      setPosicionAroY(y);
    }
  }}
/>
Agregar Llavero



{agregarLlavero && (
        <div style={{ marginTop: '10px' }}>
          <label>
            Diámetro del Agujero:
            <input type="range" min={2} max={10} step={0.1} value={diametroAgujero}
              onChange={e => { setDiametroAgujero(parseFloat(e.target.value)); setAutoPositionAro(true); }} />
            {diametroAgujero}mm
          </label>
          <br />
          <label>
            Grosor del Aro:
            <input type="range" min={1} max={5.5} step={0.1} value={grosorAro}
              onChange={e => { setGrosorAro(parseFloat(e.target.value)); setAutoPositionAro(true); }} />
            {grosorAro}mm
          </label>
          <br />
          <label>
            Posición X del Aro:
            <input type="range" min={-80} max={80} step={1} value={posicionAroX}
              onChange={e => onManualMoveAro(setPosicionAroX)(parseFloat(e.target.value))} />
            {posicionAroX}
          </label>
          <label>
            Posición Y del Aro:
            <input type="range" min={-50} max={50} step={1} value={posicionAroY}
              onChange={e => onManualMoveAro(setPosicionAroY)(parseFloat(e.target.value))} />
            {posicionAroY}
          </label>

          <br />
<label>
  Posición Z del aro:
  <input
    type="range"
    min={-20}
    max={20}
    step={0.1}
    value={posicionAroZ}
    onChange={e => { setAutoPositionAro(false); setPosicionAroZ(parseFloat(e.target.value)); }}
  />
  {posicionAroZ} mm
</label>


          <div style={{ marginTop: 8 }}>
            <button onClick={() => {
              // forzar reubicación automática
              setAutoPositionAro(true);
              const { x, y } = calcularPosicionAroAuto(nombre, dim, diametroAgujero, grosorAro, formaBase === 'contorno' ? 'contorno' : 'rect');
              setPosicionAroX(x);
              setPosicionAroY(y);
            }}>🔁 Reubicar automáticamente</button>
            <span style={{ marginLeft: 10, color: '#666' }}>
              {autoPositionAro ? 'Posicionamiento automático ON' : 'Posicionamiento manual'}
            </span>
          </div>
        </div>
      )}



<div style={{ margin: '10px 0' }}>
  <label>
    Altura del texto (relieve): 
    <input 
      type="range" 
      min={1} 
      max={20} 
      step={0.5} 
      value={dim.textHeight} 
      onChange={e => setDim(prev => ({ ...prev, textHeight: parseFloat(e.target.value) }))} 
    /> {dim.textHeight} mm
  </label>
</div>

<div style={{ margin: '10px 0' }}>
  <label>
    Altura de la base: 
    <input 
      type="range" 
      min={1} 
      max={20} 
      step={0.5} 
      value={dim.depth} 
      onChange={e => setDim(prev => ({ ...prev, depth: parseFloat(e.target.value) }))} 
    /> {dim.depth} mm
  </label>
</div>


      <h3>📷 Vista 2D / 3D</h3>
      <div style={{ height: 300 }}>
        <Canvas camera={{ position: [0, 0, 100], fov: 45 }}>
          <ambientLight intensity={0.3} />
          <Suspense fallback={null}>
          <BaseYTexto
  nombre={nombre}
  color={color}
  dim={dim}
  modo={formaBase}
  agregarLlavero={agregarLlavero}
  diametroAgujero={diametroAgujero}
  grosorAro={grosorAro}
  posicionAroX={posicionAroX}
  posicionAroY={posicionAroY}
  posicionAroZ={posicionAroZ}/>
          </Suspense>
        </Canvas>
      </div>

      <div style={{ height: 300 }}>
        <Canvas camera={{ position: [0, 0, 100], fov: 60 }}>
          <ambientLight intensity={0.4} />
          <hemisphereLight skyColor="#fff" groundColor="#888" intensity={0.6} />
          <OrbitControls enablePan enableZoom enableRotate />
          <Suspense fallback={null}>
          <Vista3D
  nombre={nombre}
  color={color}
  dim={dim}
  modo={formaBase}
  autoRotate={autoRotate}
  agregarLlavero={agregarLlavero}
  diametroAgujero={diametroAgujero}
  grosorAro={grosorAro}
  posicionAroX={posicionAroX}
  posicionAroY={posicionAroY}
  posicionAroZ={posicionAroZ}
/>

          </Suspense>
        </Canvas>
        <button onClick={() => setAutoRotate(!autoRotate)} style={{ marginTop: 10 }}>
          {autoRotate ? '⏸️ Detener rotación' : '🔁 Rotar'}
        </button>
      </div>
    </div>
  );
}
